import { isNull } from 'lodash';

import { getMediaLists } from '@/services/mediaManagement/index';
import { IMediaManagement } from '@/services/mediaManagement/interface';

const MEDIA_STORAGE_KEY = 'BV_MEDIA_LIST';

export const getMediaMap = async () => {
  const [res, error] = await getMediaLists();

  if (res) {
    let emptyDataFlag = false;

    // 从 localStorage 中获取媒体数据
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);

    // 如果 localStorage 中没有媒体数据，或者媒体数据为空，则设置 emptyDataFlag 为 true
    if (isNull(tempMediaList) || tempMediaList === '[]') {
      emptyDataFlag = true;
    }

    // 将媒体数据存入 localStorage
    localStorage.setItem(MEDIA_STORAGE_KEY, JSON.stringify(res));

    // 如果 emptyDataFlag 为 true，则刷新页面
    if (emptyDataFlag) {
      console.log('触发媒体空数据界面刷新');
      window.location.reload();
    }
  } else {
    console.error('拉取媒体数据失败', error);
  }
};

// 用于访问媒体数据的 Store
export const mediaStore = {
  get allMediaList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempParseList = JSON.parse(tempMediaList) as IMediaManagement.Common.MediaMetaInfo[];

      return tempParseList?.map((item) => ({
        value: item.media_key,
        title: item.media_name,
        ...item,
      }));
    } else {
      return [];
    }
  },
  get smallMediaList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList) as IMediaManagement.Common.MediaMetaInfo[];

      return tempData
        .filter((item) => item.new_media === IMediaManagement.Common.MediaFlagEnum.YES)
        .map((item) => ({
          value: item.media_key,
          title: item.media_name,
          ...item,
        }));
    } else {
      return [];
    }
  },
  get allMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return createMediaMap(tempData);
    } else {
      return {};
    }
  },
  get smallMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const smallMediaList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.new_media === IMediaManagement.Common.MediaFlagEnum.YES;
      });
      return createMediaMap(smallMediaList);
    } else {
      return {};
    }
  },
  get mainMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const mainMediaList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.new_media === IMediaManagement.Common.MediaFlagEnum.NO;
      });

      return createMediaMap(mainMediaList);
    } else {
      return {};
    }
  },
  get allMediaStringMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return createStringMap(tempData);
    } else {
      return {};
    }
  },
  get allMediaValueMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return createValueMap(tempData);
    } else {
      return {};
    }
  },
  get mainMediaValueMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const mainMediaList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.new_media === IMediaManagement.Common.MediaFlagEnum.NO;
      });
      return createValueMap(mainMediaList);
    } else {
      return {};
    }
  },
  get smallMediaValueMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const smallMediaList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.new_media === IMediaManagement.Common.MediaFlagEnum.YES;
      });
      return createValueMap(smallMediaList);
    } else {
      return {};
    }
  },
  get mediaAssetMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return createAssetMap(tempData);
    } else {
      return {};
    }
  },
  // 支持开户的所有媒体列表
  get supportAccountOpenMediaList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return tempData
        .filter(
          (item: IMediaManagement.Common.MediaMetaInfo) =>
            item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES,
        )
        .map((item: IMediaManagement.Common.MediaMetaInfo) => ({
          value: item.media_key,
          title: item.media_name,
          ...item,
        }));
    } else {
      return [];
    }
  },

  // 支持开户的小媒体列表
  get smallMediaSupportAccountOpenList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList) as IMediaManagement.Common.MediaMetaInfo[];

      return tempData
        .filter(
          (item: IMediaManagement.Common.MediaMetaInfo) =>
            item.new_media === IMediaManagement.Common.MediaFlagEnum.YES &&
            item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES,
        )
        .map((item: IMediaManagement.Common.MediaMetaInfo) => ({
          value: item.media_key,
          title: item.media_name,
          ...item,
        }));
    } else {
      return [];
    }
  },

  // 支持开户的主要媒体列表
  get mainMediaSupportAccountOpenList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList) as IMediaManagement.Common.MediaMetaInfo[];

      return tempData
        .filter(
          (item: IMediaManagement.Common.MediaMetaInfo) =>
            item.new_media === IMediaManagement.Common.MediaFlagEnum.NO &&
            item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES,
        )
        .map((item: IMediaManagement.Common.MediaMetaInfo) => ({
          value: item.media_key,
          title: item.media_name,
          ...item,
        }));
    } else {
      return [];
    }
  },

  // 支持开户的所有媒体映射表
  get supportAccountOpenMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const supportAccountOpenList = tempData.filter(
        (item: IMediaManagement.Common.MediaMetaInfo) => {
          return item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES;
        },
      );
      return createMediaMap(supportAccountOpenList);
    } else {
      return {};
    }
  },

  // 支持开户的小媒体映射表
  get smallMediaSupportAccountOpenMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const filteredList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return (
          item.new_media === IMediaManagement.Common.MediaFlagEnum.YES &&
          item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES
        );
      });
      return createMediaMap(filteredList);
    } else {
      return {};
    }
  },

  // 支持开户的主要媒体映射表
  get mainMediaSupportAccountOpenMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const filteredList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return (
          item.new_media === IMediaManagement.Common.MediaFlagEnum.NO &&
          item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES
        );
      });
      return createMediaMap(filteredList);
    } else {
      return {};
    }
  },
};

// 工具函数：创建映射表
function createMediaMap(list: IMediaManagement.Common.MediaMetaInfo[]) {
  return list.reduce((acc, cur) => {
    acc[cur.media_key] = cur.media_name;
    return acc;
  }, {} as Record<number, string>);
}

function createStringMap(list: IMediaManagement.Common.MediaMetaInfo[]) {
  return list.reduce((acc, cur) => {
    acc[cur.media_key_value] = cur.media_name;
    return acc;
  }, {} as Record<string, string>);
}

function createValueMap(list: IMediaManagement.Common.MediaMetaInfo[]) {
  return list.reduce((acc, cur) => {
    acc[cur.media_key_value] = cur.media_key;
    return acc;
  }, {} as Record<string, number>);
}

function createAssetMap(list: IMediaManagement.Common.MediaMetaInfo[]) {
  return list.reduce((acc, cur) => {
    if (!cur.media_asset_name) return acc;
    acc[cur.media_key] = cur.media_asset_name;
    acc[cur.media_key_value] = cur.media_asset_name;
    acc[cur.media_name] = cur.media_asset_name;
    acc[cur.media_name_en] = cur.media_asset_name;
    return acc;
  }, {} as Record<string, string[]>);
}

export const findMediaByKey = (key: number, isNewMedia?: boolean) => {
  return (isNewMedia ? mediaStore.smallMediaList : mediaStore.allMediaList).find(
    (item) => item.media_key === key,
  );
};

export const findMediaByName = (name: string, isNewMedia?: boolean) => {
  return (isNewMedia ? mediaStore.smallMediaList : mediaStore.allMediaList).find(
    (item) => item.media_name === name,
  );
};
